import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Users, Shield, X, Edit2, Trash2, Save, Mail, Receipt, ShieldCheck, Database, UserCheck } from 'lucide-react';
import MailSettingsTab from './MailSettingsTab';
import EasybillTab from './EasybillTab';
import PermissionsTab from './PermissionsTab';
import RolesManagementTab from './RolesManagementTab';
import MaintenanceTab from './MaintenanceTab';
import CustomersManagementTab from '../../components/settings/CustomersManagementTab';

export default function SettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'technik' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', password: '' });
  const isAdmin = user?.role === 'admin';
  const actions = useMemo(() => new Set(user?.actions || []), [user]);
  // Innendienst & Co. dürfen B2B-Kunden verwalten, wenn die Action gesetzt ist.
  const canManageCustomers = isAdmin || actions.has('customers.read') || actions.has('customers.write');

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    const res = await authFetch('/api/auth/users');
    if (res.ok) setUsers(await res.json());
  }, [isAdmin]);

  const loadRoles = useCallback(async () => {
    const res = await authFetch('/api/roles');
    if (res.ok) setRoles(await res.json());
  }, []);

  useEffect(() => { loadUsers(); loadRoles(); }, [loadUsers, loadRoles]);

  // Map role.id → label for display, fallback to capitalized id
  const roleLabel = (id) => {
    const r = roles.find(x => x.id === id);
    return r ? r.label : (id || '').replace(/(^|-)\w/g, m => m.toUpperCase().replace('-', ' '));
  };

  const createUser = async () => {
    setError(''); setSuccess('');
    if (!form.email || !form.password || !form.name) { setError('Bitte alle Felder ausfüllen'); return; }
    const res = await authFetch('/api/auth/register', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setSuccess(`Benutzer ${form.email} wurde angelegt`);
      setForm({ email: '', password: '', name: '', role: 'technik' });
      setShowNew(false);
      loadUsers();
    } else {
      const data = await res.json();
      setError(data.detail || 'Fehler beim Anlegen');
    }
  };

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-xl font-bold text-[#32373c] mb-1" data-testid="settings-title">Einstellungen</h1>
      <p className="text-xs text-gray-400 mb-4">Benutzer, Rollen und Mailserver verwalten</p>

      {/* Tabs */}
      {(isAdmin || canManageCustomers) && (
        <div className="flex gap-1 border-b border-gray-200 mb-6 flex-wrap">
          {isAdmin && (
          <button
            onClick={() => setTab('users')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'users' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4 inline mr-1.5" /> Benutzer & Rollen
          </button>)}
          {isAdmin && (
          <button
            onClick={() => setTab('mail')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'mail' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="tab-mail"
          >
            <Mail className="w-4 h-4 inline mr-1.5" /> Mailserver
          </button>)}
          {isAdmin && (
          <button
            onClick={() => setTab('easybill')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'easybill' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="tab-easybill"
          >
            <Receipt className="w-4 h-4 inline mr-1.5" /> Easybill
          </button>)}
          {isAdmin && (
          <button
            onClick={() => setTab('permissions')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'permissions' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="tab-permissions"
          >
            <ShieldCheck className="w-4 h-4 inline mr-1.5" /> Rollen-Rechte
          </button>)}
          {isAdmin && (
          <button
            onClick={() => setTab('roles')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'roles' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="tab-roles"
          >
            <Shield className="w-4 h-4 inline mr-1.5" /> Rollen verwalten
          </button>)}
          {canManageCustomers && (
          <button
            onClick={() => setTab('customers')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'customers' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="tab-customers"
          >
            <UserCheck className="w-4 h-4 inline mr-1.5" /> Kunden (B2B-Shop)
          </button>)}
          {isAdmin && (
          <button
            onClick={() => setTab('maintenance')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'maintenance' ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="tab-maintenance"
          >
            <Database className="w-4 h-4 inline mr-1.5" /> Wartung & Backups
          </button>)}
        </div>
      )}

      {isAdmin && tab === 'mail' && <MailSettingsTab />}
      {isAdmin && tab === 'easybill' && <EasybillTab />}
      {isAdmin && tab === 'permissions' && <PermissionsTab />}
      {isAdmin && tab === 'roles' && <RolesManagementTab onChange={loadRoles} />}
      {canManageCustomers && tab === 'customers' && <CustomersManagementTab />}
      {isAdmin && tab === 'maintenance' && <MaintenanceTab />}

      {tab === 'users' && (<>
      {/* Roles overview */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-[#32373c] mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-[#9ec043]" /> Rollen</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {roles.map(r => {
            const isMyRole = user?.role === r.id;
            return (
              <div key={r.id} className={`rounded-lg border p-3 ${isMyRole ? 'bg-[#9ec043]/10 border-[#9ec043]' : 'bg-white border-gray-200'}`}>
                <div className={`text-sm font-medium capitalize ${isMyRole ? 'text-[#9ec043]' : 'text-[#32373c]'}`}>{r.label}</div>
                <p className="text-xs text-gray-400 mt-0.5">{r.description}</p>
                {isMyRole && <span className="text-[10px] text-[#9ec043] mt-1 inline-block">Ihre Rolle</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* User management - admin only */}
      {isAdmin && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2"><Users className="w-4 h-4 text-[#9ec043]" /> Benutzer ({users.length})</h2>
            <Button onClick={() => setShowNew(true)} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-new-user"><Plus className="w-4 h-4 mr-1" /> Neuer Benutzer</Button>
          </div>

          {success && <div className="text-sm text-green-600 bg-green-50 rounded p-3 mb-3 border border-green-200" data-testid="settings-success">{success}</div>}
          {error && <div className="text-sm text-red-600 bg-red-50 rounded p-3 mb-3 border border-red-200" data-testid="settings-error">{error}</div>}

          {/* New user form */}
          {showNew && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm" data-testid="new-user-form">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-semibold text-[#32373c]">Neuen Benutzer anlegen</h3>
                <button onClick={() => { setShowNew(false); setError(''); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-xs text-gray-500">Name</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Max Mustermann" className="mt-1 bg-white border-gray-300 text-sm" data-testid="new-user-name" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">E-Mail</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="name@ees-energiespeicher.de" className="mt-1 bg-white border-gray-300 text-sm" data-testid="new-user-email" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Passwort</Label>
                  <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Sicheres Passwort" className="mt-1 bg-white border-gray-300 text-sm" data-testid="new-user-password" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Rolle</Label>
                  <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid="new-user-role">
                    {roles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={createUser} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-create-user">Benutzer anlegen</Button>
                <Button variant="outline" onClick={() => { setShowNew(false); setError(''); }} className="text-sm">Abbrechen</Button>
              </div>
            </div>
          )}

          {/* User list */}
          <div className="space-y-2">
            {users.map(u => (
              <div key={u.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`user-row-${u.email}`}>
                {editingUser === u.id ? (
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs text-gray-500">Name</Label><Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-white border-gray-300 text-sm" data-testid="edit-user-name" /></div>
                      <div><Label className="text-xs text-gray-500">E-Mail</Label><Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} className="mt-1 bg-white border-gray-300 text-sm" data-testid="edit-user-email" /></div>
                      <div><Label className="text-xs text-gray-500">Neues Passwort (leer = unverändert)</Label><Input type="password" value={editForm.password} onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))} placeholder="Nur bei Änderung ausfüllen" className="mt-1 bg-white border-gray-300 text-sm" data-testid="edit-user-password" /></div>
                      <div><Label className="text-xs text-gray-500">Rolle</Label><select value={editForm.role} onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid="edit-user-role">{roles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}</select></div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={async () => {
                        setError(''); setSuccess('');
                        const body = { name: editForm.name, email: editForm.email, role: editForm.role };
                        if (editForm.password.trim()) body.password = editForm.password;
                        const res = await authFetch(`/api/auth/users/${u.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
                        if (res.ok) { setSuccess(`Benutzer ${editForm.email} wurde aktualisiert`); setEditingUser(null); loadUsers(); }
                        else { const d = await res.json(); setError(d.detail || 'Fehler'); }
                      }} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-save-user"><Save className="w-3.5 h-3.5 mr-1" /> Speichern</Button>
                      <Button variant="outline" onClick={() => setEditingUser(null)} className="text-sm">Abbrechen</Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                         style={{
                           backgroundColor: (u.calendarColor || '#9ec043') + '22',
                           color: u.calendarColor || '#9ec043',
                         }}
                         title={u.calendarColor ? `Kalender-Farbe: ${u.calendarColor}` : 'Keine Kalender-Farbe gesetzt'}
                    >
                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#32373c]">{u.name}</div>
                      <div className="text-xs text-gray-400">{u.email}</div>
                    </div>
                    <input
                      type="color"
                      value={u.calendarColor || '#9ec043'}
                      onChange={async (e) => {
                        const color = e.target.value;
                        const res = await authFetch(`/api/auth/users/${u.id}/calendar-color`, {
                          method: 'PUT', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ color }),
                        });
                        if (res.ok) { setSuccess(`Kalender-Farbe von ${u.name} aktualisiert`); loadUsers(); }
                      }}
                      title="Kalender-Farbe für diesen Mitarbeiter"
                      className="w-6 h-6 rounded cursor-pointer border border-gray-200 shrink-0"
                      data-testid={`color-picker-${u.id}`}
                    />
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize shrink-0">{roleLabel(u.role)}</span>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => { setEditingUser(u.id); setEditForm({ name: u.name, email: u.email, role: u.role, password: '' }); setError(''); setSuccess(''); }} className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded" data-testid={`btn-edit-user-${u.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                      {u.email !== user?.email && <button onClick={async () => {
                        if (!window.confirm(`Benutzer "${u.name}" wirklich löschen?`)) return;
                        setError(''); setSuccess('');
                        const res = await authFetch(`/api/auth/users/${u.id}`, { method: 'DELETE' });
                        if (res.ok) { setSuccess(`Benutzer ${u.email} wurde gelöscht`); loadUsers(); }
                        else { const d = await res.json(); setError(d.detail || 'Fehler'); }
                      }} className="p-1.5 text-gray-400 hover:text-red-500 rounded" data-testid={`btn-delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!isAdmin && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
          <p className="text-sm text-gray-500">Die Benutzerverwaltung ist nur für Administratoren zugänglich.</p>
          <div className="mt-4 bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-xs text-gray-400 mb-1">Ihr Konto</p>
            <p className="text-sm font-medium text-[#32373c]">{user?.name}</p>
            <p className="text-xs text-gray-400">{user?.email} — <span className="capitalize">{roleLabel(user?.role)}</span></p>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
